
import { useId, type ReactNode } from 'react';
import { cx } from '@client/shared/lib/cx.ts';
import styles from './Field.module.css';

export interface FieldProps {
  label: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  optional?: boolean;
  className?: string;
  children: (props: {
    id: string;
    'aria-invalid': boolean | undefined;
    'aria-describedby': string | undefined;
    'data-invalid': boolean;
  }) => ReactNode;
}

export function Field({ label, error, hint, optional, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const invalid = Boolean(error);
  const describedBy = cx(hint ? hintId : undefined, invalid ? errorId : undefined) || undefined;

  return (
    <div className={cx(styles.field, className)}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {optional && <span className={styles.optional}>Optional</span>}
      </div>

      {children({
        id,
        'aria-invalid': invalid || undefined,
        'aria-describedby': describedBy,
        'data-invalid': invalid,
      })}

      {hint && !invalid && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {invalid && (
        <p id={errorId} className={styles.message} role="alert">
          <ErrorMark />
          {error}
        </p>
      )}
    </div>
  );
}

function ErrorMark() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0, marginTop: '0.12em' }}
    >
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5v4.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="11.4" r="0.85" fill="currentColor" />
    </svg>
  );
}
